import Phaser from 'phaser';
import type { Invite } from '../../meta/online/hub';
import { haptics } from '../../platform/haptics';
import { gameAudio } from '../audio/audio';
import { joinInvite } from '../hub';
import { whoText } from '../nicknames';
import { MODE_WORDS } from '../modeWords';
import { bevelButton, navyPanel } from '../ui/chrome';
import { label, UI } from '../ui/text';

export interface InviteData {
  invite: Invite;
}

const SIZE_NAME = { quick: 'QUICK', normal: 'NORMAL', long: 'LONG' } as const;

/**
 * A friend's invite, just arrived (docs/FRIENDS.md, step 2): a card over whatever screen is up (a
 * menu, or a lawn's results), with JOIN and LATER. LATER keeps it in the INBOX until its room
 * closes; JOIN goes to the room's lobby.
 */
export class InviteScene extends Phaser.Scene {
  private invite: Invite | null = null;

  constructor() {
    super('Invite');
  }

  init(data: InviteData): void {
    this.invite = data.invite;
  }

  create(): void {
    const invite = this.invite;
    if (!invite) {
      this.scene.stop();
      return;
    }
    this.scene.bringToTop();
    const { width, height } = this.scale.gameSize;
    // The screen behind waits: a dim veil that takes every tap.
    this.add.rectangle(0, 0, width, height, 0x000000, 0.55).setOrigin(0).setInteractive();
    const g = this.add.graphics();
    const h = 92;
    const top = Math.round((height - h) / 2);
    navyPanel(g, 8, top, width - 16, h);
    const who = whoText(invite.from.player, invite.from.name);
    const what = MODE_WORDS[invite.mode].wants;
    label(this, width / 2, top + 10, who, 1, UI.accent).setOrigin(0.5, 0);
    label(this, width / 2, top + 22, what, 1, UI.text).setOrigin(0.5, 0);
    label(this, width / 2, top + 34, `${SIZE_NAME[invite.size]} lawn`, 1, UI.dim).setOrigin(0.5, 0);
    const half = Math.floor((width - 36) / 2);
    bevelButton(this, 14, top + 56, half, 24, 'JOIN', () => this.join(invite), true);
    bevelButton(this, 22 + half, top + 56, half, 24, 'LATER', () => this.scene.stop());
    gameAudio.honk();
    haptics.impact();
  }

  /** Android's back button: LATER (the invite waits in the INBOX). */
  onBack(): void {
    this.scene.stop();
  }

  private join(invite: Invite): void {
    const game = this.game;
    this.scene.stop();
    joinInvite(game.scene.getScenes(true)[0] ?? this, invite);
  }
}
